import { FileManager, FileAsset, FileUploadParams } from '@otp/ui';
import { useMutation } from '@apollo/client';
import { FileElement } from '@otp/ui/src';
import { CreatePresignedUploadUrlDocument } from 'src/graphql/generated/graphql';
import { useState } from 'react';

interface AttachmentsFieldProps {
  onChange: (files: FileElement[]) => void;
  value: FileElement[];
  reverseDirection?: boolean;
  buttonContent?:
    | React.ReactNode
    | ((props: { disabled?: boolean }) => React.ReactNode);
  buttonFullWidth?: boolean;
  fileDescription?: string | boolean;
  fileAccept?: string[];
  readonly?: boolean;
  error?: boolean;
  showLoader?: boolean;
  maxFileCount?: number;
}

export const AttachmentsField = ({
  onChange,
  value,
  reverseDirection,
  buttonContent,
  buttonFullWidth,
  fileDescription,
  fileAccept,
  readonly = false,
  error,
  maxFileCount,
  showLoader,
}: AttachmentsFieldProps) => {
  // we need to have controlled state here because of multiple file uploads issue, when parent state is not updated fast enough
  const [assets, setAssets] = useState<FileElement[]>(value || []);
  const [getPresignedUploadFileUrl] = useMutation(
    CreatePresignedUploadUrlDocument,
  );

  const getFileUploadParams = async ({ fileName }: { fileName?: string }) => {
    const urlData = await getPresignedUploadFileUrl({
      variables: {
        fileName,
      },
    });
    const { uploadUrl, getUrl, key } =
      urlData.data?.createPresignedUploadUrl || {};

    if (!uploadUrl || !key) {
      return undefined as unknown as FileUploadParams;
    }

    return {
      uploadUrl,
      getUrl,
      key,
      method: 'PUT' as FileUploadParams['method'],
    };
  };

  const onFileUploaded = (asset: FileAsset) => {
    setAssets((assets) => {
      const newValue = [...assets, asset];
      onChange(newValue);
      return newValue;
    });
  };

  const onFileDelete = (indexToDelete: number) => {
    if (indexToDelete < 0 || indexToDelete >= assets.length) {
      return;
    }

    setAssets((assets) => {
      const newValue = [
        ...assets.slice(0, indexToDelete),
        ...assets.slice(indexToDelete + 1),
      ];
      onChange(newValue);
      return newValue;
    });
  };

  return (
    <FileManager
      files={assets}
      readonly={readonly}
      reverseDirection={reverseDirection}
      buttonContent={buttonContent}
      buttonFullWidth={buttonFullWidth}
      fileDescription={fileDescription}
      fileAccept={fileAccept}
      getUploadParams={getFileUploadParams}
      onFileUploaded={onFileUploaded}
      onFileDelete={onFileDelete}
      error={error}
      maxFileCount={maxFileCount}
      showLoader={showLoader}
    />
  );
};
